import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import agGridConfig from '../../../agGridConfig';

import columnDefinitions from './columnDefinitions';
import useSelectAgGridRow from '../../../shared/hooks/useSelectAgGridRow.hook';
import { useCreateCountry, useDeleteCountry, useUpdateCountry } from '../../../shared/mutations/countries';
import { useCountriesList } from '../../../shared/queries/countries';
import { currentPeriodSelector, environmentSuffixSelector, periodOrCurrentSelector } from '../../../shared/store/selectors';

import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../../userData/store/selectors';

import AdminImportTab from '../shared/adminImportTab.component';
import useAddEditModalControls from '../shared/useAddEditModalControls';
import useRemoveModalControls from '../shared/useRemoveModalControls';
import EditCurrencyCodeAlertContent from './editCurrencyCodeAlertContent.component';

import { Country, CountryFormData, SaveEntityFunction } from '../types';
import AddEditCountryModal from './addEditCountryModal.component';
import CountriesAdminColumnRendererFactory from './countriesAdminColumnRenderer.component';
import DeleteCountryModal from './deleteCountryModal.component';
import { useAdminCurrencies } from '../../../shared/queries/currencies';
import alertTypes from '../../../shared/alertTypes';

type CountriesTabProps = {
  canModify: boolean,
  hideDecimals: boolean,
  shouldDisplayZero: boolean,
  environmentSuffix: string,
  negativeMinus: boolean,
}

const CountriesTab = ({
  canModify,
  hideDecimals,
  shouldDisplayZero,
  environmentSuffix,
  negativeMinus,
}: CountriesTabProps) => {
  const {
    selectAgGridRow,
    selectAgGridRowByIndex,
  } = useSelectAgGridRow(agGridConfig.adminClientAdministrationCountry);

  const {
    hideModalForAddEdit,
    showModalForAdd,
    showModalForEdit,
    entityToEdit,
    isAddEditModalVisible,
  } = useAddEditModalControls<Country>();

  const {
    hideModalForRemove,
    showModalForRemove,
    entityToRemove,
    rowToRemoveIndex,
    isRemoveModalVisible,
  } = useRemoveModalControls<Country>();

  const {
    isFetching: isFetchingLookUpData,
    data: currencies,
  } = useAdminCurrencies();
  const currencyCodeOptions = useMemo(
    () => currencies.map(({ currencyCode }) => ({ value: currencyCode, label: currencyCode })),
    [currencies]
  );

  const {
    isFetching: isFetchingEntities,
    data: entities,
    refetch: fetchEntities,
  } = useCountriesList();

  const { mutate: addEntity, isLoading: isAddingEntity } = useCreateCountry();
  const { mutate: updateEntity, isLoading: isUpdatingEntity } = useUpdateCountry();
  const { mutate: removeEntity, isLoading: isRemovingEntity } = useDeleteCountry(() => {
    hideModalForRemove();
    selectAgGridRowByIndex(rowToRemoveIndex ? rowToRemoveIndex - 1 : 0);
  });

  const onSaveSuccess = useCallback((formData, { keepOpenAfterSaving, callback }) => {
    if (!keepOpenAfterSaving) {
      hideModalForAddEdit();
      selectAgGridRow(formData);
    }

    if (callback) {
      return callback();
    }
  }, [hideModalForAddEdit, selectAgGridRow]);

  const saveEntity: SaveEntityFunction<CountryFormData> = useCallback(
    async (formData, openForEdit, options) => {
      if (openForEdit) {
        updateEntity(
          formData,
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      } else {
        addEntity(
          formData,
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      }
    }, [addEntity, updateEntity, onSaveSuccess]
  );

  const isSavingEntity = isAddingEntity || isUpdatingEntity;

  const adminColumnRenderer = useMemo(() => CountriesAdminColumnRendererFactory({
    showModalForEdit,
    showModalForRemove,
  }), [
    showModalForEdit,
    showModalForRemove,
  ]);

  return (
    <AdminImportTab<
      Country,
      CountryFormData
    >
      className="client-admin-countries-tab"
      pageTitle="Countries"
      hasPeriodControl={false}
      canModify={canModify}

      AddEditEntityModal={AddEditCountryModal}
      RemoveEntityModal={DeleteCountryModal}
      adminColumnRenderer={adminColumnRenderer}
      addEntityMessage="Add Country"
      columnDefs={columnDefinitions}

      isFetchingEntities={isFetchingEntities || isFetchingLookUpData}
      fetchEntities={fetchEntities}
      entities={entities}

      saveEntity={saveEntity}
      isSavingEntity={isSavingEntity}

      removeEntity={removeEntity}
      isRemovingEntity={isRemovingEntity}

      hideDecimals={hideDecimals}
      shouldDisplayZero={shouldDisplayZero}
      environmentSuffix={environmentSuffix}
      negativeMinus={negativeMinus}


      isAddEditModalVisible={isAddEditModalVisible}
      entityToEdit={entityToEdit}

      isRemoveModalVisible={isRemoveModalVisible}
      entityToRemove={entityToRemove}
      rowToRemoveIndex={rowToRemoveIndex}

      showModalForAdd={showModalForAdd}
      hideModalForAddEdit={hideModalForAddEdit}
      hideModalForRemove={hideModalForRemove}

      additionalData={{
        lookUpData: { currencyCodeOptions },
      }}
      alert={{
        content: <EditCurrencyCodeAlertContent />,
        show: true,
        alertType: alertTypes.WARNING,
      }}
    />
  );
};


const mapStateToProps = (state: { shared: any, user: any }) => ({
  period: periodOrCurrentSelector(state),
  currentPeriod: currentPeriodSelector(state),

  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),
});

export default connect(mapStateToProps)(CountriesTab);
